/**
 * La «ruta de formación» del landing: la escalera de programas, contada para un
 * visitante. Usa el mismo `buildProgramLadder` que la pantalla académica, así
 * que el landing y el módulo académico no pueden contar dos escaleras distintas.
 *
 * Si la escalera tiene algún problema (un requisito que no existe, un ciclo,
 * años declarados a medias) NO se dibuja: el landing vuelve a la lista de
 * programas. Una escalera rota en la página pública confundiría a quien postula.
 */
import { buildProgramLadder, type LadderProgram } from '@dosfilos/domain';
import type { PublicCatalog, PublicCatalogProgram } from '@dosfilos/application';

export interface PathRung {
  program: PublicCatalogProgram;
  /** Para el enlace: el código en minúsculas, o el id si el código se repite. */
  slug: string;
  startColumn: number;
  span: number;
  depth: number;
  /** Años que ocupa, cuando la escalera está en años. */
  years: number[];
  courseIds: string[];
  credits: number;
  /** El programa en que se apoya, si hay. */
  requires: PublicCatalogProgram | null;
  /** Título acumulativo: créditos de toda la ruta que exige. */
  cumulativeCredits: number | null;
  includedProgramIds: string[];
}

export interface PublicPath {
  columns: { year: number | null; label: string | null }[];
  rungs: PathRung[];
  /** Programas activos que no forman parte de la escalera. */
  others: PublicCatalogProgram[];
  totalCredits: number;
}

export function uniqueCourseCredits(
  ids: Iterable<string>,
  courses: PublicCatalog['courses'],
): number {
  let total = 0;
  for (const id of new Set(ids)) total += courses[id]?.credits ?? 0;
  return total;
}

export function programSlug(program: PublicCatalogProgram, all: PublicCatalogProgram[]): string {
  const code = program.code?.trim().toLowerCase();
  if (!code) return program.id;
  const clash = all.filter((p) => p.code?.trim().toLowerCase() === code).length > 1;
  return clash || !/^[a-z0-9-]+$/.test(code) ? program.id : code;
}

export function findProgramBySlug(
  catalog: PublicCatalog,
  slug: string,
): PublicCatalogProgram | undefined {
  const s = slug.trim().toLowerCase();
  return catalog.programs.find((p) => programSlug(p, catalog.programs) === s || p.id === slug);
}

export function programHref(basePath: string, slug: string): string {
  return `${basePath}/programas/${encodeURIComponent(slug)}`;
}

function toLadderProgram(p: PublicCatalogProgram): LadderProgram {
  return {
    id: p.id,
    name: p.name,
    status: 'active',
    ...(p.stacksOnProgramId ? { stacksOnProgramId: p.stacksOnProgramId } : {}),
    ...(p.includesStackedPrograms ? { includesStackedPrograms: true } : {}),
    ...(p.curriculum
      ? {
          currentCatalogYear: p.curriculum.catalogYear,
          curriculumVersions: [
            {
              catalogYear: p.curriculum.catalogYear,
              terms: p.curriculum.terms.map((t) => ({
                termOffset: t.termOffset,
                recommendedCourseIds: t.courseIds,
                ...(t.year !== undefined ? { year: t.year } : {}),
                ...(t.semester !== undefined ? { semester: t.semester } : {}),
                ...(t.label ? { label: t.label } : {}),
                ...(t.electiveSlots ? { electiveSlots: t.electiveSlots } : {}),
              })),
            },
          ],
        }
      : { curriculumVersions: [] }),
  };
}

export function buildPublicPath(catalog: PublicCatalog): PublicPath | null {
  const ladder = buildProgramLadder(catalog.programs.map(toLadderProgram));
  if (ladder.problems.length > 0) return null;
  const chain = ladder.rows.filter((r) => r.inChain);
  if (chain.length < 2) return null;

  const byId = new Map(catalog.programs.map((p) => [p.id, p]));
  const ownCourses = new Map(
    ladder.rows.map((r) => [r.programId, [...new Set(r.terms.flatMap((t) => t.courseIds))]]),
  );

  const rungs: PathRung[] = chain.map((r) => {
    const program = byId.get(r.programId)!;
    const courseIds = ownCourses.get(r.programId) ?? [];
    const years: number[] = [];
    for (let c = r.startColumn; c < r.startColumn + r.termCount; c++) {
      const y = ladder.columns[c]?.year;
      if (y !== undefined) years.push(y);
    }
    const cumulativeIds = r.includedProgramIds.length
      ? [...r.includedProgramIds.flatMap((id) => ownCourses.get(id) ?? []), ...courseIds]
      : null;
    return {
      program,
      slug: programSlug(program, catalog.programs),
      startColumn: r.startColumn,
      span: r.termCount,
      depth: r.depth,
      years,
      courseIds,
      credits: uniqueCourseCredits(courseIds, catalog.courses),
      requires: program.stacksOnProgramId ? (byId.get(program.stacksOnProgramId) ?? null) : null,
      cumulativeCredits: cumulativeIds ? uniqueCourseCredits(cumulativeIds, catalog.courses) : null,
      includedProgramIds: r.includedProgramIds,
    };
  });

  const inPath = new Set(rungs.map((r) => r.program.id));
  return {
    columns: ladder.columns.map((c) => ({ year: c.year ?? null, label: c.label })),
    rungs,
    others: catalog.programs.filter((p) => !inPath.has(p.id)),
    totalCredits: uniqueCourseCredits(
      rungs.flatMap((r) => r.courseIds),
      catalog.courses,
    ),
  };
}
